import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BankScopeField from '../../components/BankScopeField';
import LotteryCodeField from '../../components/LotteryCodeField';
import DateField from '../../components/DateField';
import { fetchResumeBanqueLottery } from '../../api/resumeBanqueLottery';
import { todayISO } from '../../utils/date';
import { bankLabel } from '../../utils/bank';
import './ResumeBanqueLotteryPage.css';

const money = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ResumeBanqueLotteryPage = () => {
  const [bankId, setBankId] = useState('');
  const [bankName, setBankName] = useState('');
  const [lottery, setLottery] = useState('');
  const [lotteryName, setLotteryName] = useState('');
  const [date, setDate] = useState(todayISO());

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!bankId || !lottery) { setError('Choisis une banque et une loterie.'); return; }
    setLoading(true);
    setError('');
    try {
      setData(await fetchResumeBanqueLottery({ bank: bankId, tirage: lottery, date }));
    } catch (err) {
      setError('Recherche impossible.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <p className="rbl-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/rapports" className="bc-link">Rapports</Link>
        {' >> '}
        <span className="bc-current">Resume de la Banque-Lottery</span>
      </p>

      <div className="rbl-card">
        <div className="rbl-card-header">
          <span className="rbl-card-title">Resume de Banque et Loterie</span>
        </div>
        <div className="rbl-card-body">
          <div className="rbl-radio-row">
            <label className="rbl-radio-label">
              <input type="radio" name="filter" defaultChecked readOnly />
              Banque
            </label>
          </div>

          <form onSubmit={handleSearch} className="rbl-form">
            <div className="rbl-row">
              <label className="rbl-label">Banque</label>
              <BankScopeField
                id={bankId}
                name={bankName}
                onResolve={(id, name) => { setBankId(id); setBankName(name); }}
                inputClass="rbl-input rbl-input-sm"
                iconClass="rbl-icon-btn"
                nameClass="rbl-name"
                errClass="rbl-lookup-err"
              />
            </div>

            <div className="rbl-row">
              <label className="rbl-label">Loterie</label>
              <LotteryCodeField
                value={lottery}
                name={lotteryName}
                onChange={(code) => { setLottery(code); setLotteryName(''); }}
                onResolve={(code, nm) => { setLottery(code); setLotteryName(nm); }}
                inputClass="rbl-input rbl-input-sm"
                iconClass="rbl-icon-btn"
                nameClass="rbl-name"
              />
            </div>

            <div className="rbl-row">
              <label className="rbl-label">Date</label>
              <DateField className="rbl-input rbl-input-date" value={date} onChange={setDate} />
            </div>

            {error && <p className="rbl-error">{error}</p>}
            <button type="submit" className="rbl-search-btn" disabled={loading}>
              {loading ? 'Recherche...' : 'Obtenir'}
            </button>
          </form>
        </div>
      </div>

      {data && (
        <div className="rbl-receipt">
          <p className="rbl-title">Resume de Banque et Loterie {data.date}</p>
          <p className="rbl-bank-line">Banca : {bankLabel(data.bank_seq, data.bank_name)}{data.bank_username ? ` (${data.bank_username})` : ''}</p>

          {data.lotteries.length === 0 && <p className="rbl-empty">Aucune vente ce jour pour cette banque.</p>}

          {data.lotteries.map((lot) => (
            <div key={lot.tirage_code} className={`rbl-lottery-block${lot.selected ? ' rbl-lottery-selected' : ''}`}>
              <p className="rbl-lottery-title">{lot.tirage_code} =&gt; {lot.tirage_name}</p>

              <div className="rbl-line">
                <span>NUM : {lot.numbers ?? '—'}</span>
                <span>Fiches Vendu : {lot.fiches_vendu}</span>
              </div>
              <div className="rbl-line">
                <span>WIN4 : {lot.win4 ?? '—'}</span>
                <span>Total Paris : {lot.total_paris}</span>
              </div>

              <div className="rbl-amount-row">
                <span>Vendu =</span>
                <span>{money(lot.vendu)}</span>
              </div>

              {lot.selected && (
                <>
                  <div className="rbl-amount-row">
                    <span>Gagne =</span>
                    <span>{money(lot.gagne)}</span>
                  </div>
                  <hr className="rbl-dash" />
                  <div className="rbl-amount-row rbl-bold">
                    <span>Brut =</span>
                    <span>{money(lot.brut)}</span>
                  </div>

                  <p className="rbl-section-title">-------Ticket Gagner-------</p>
                  {lot.tickets_gagnants.length === 0 ? (
                    <p className="rbl-empty-sm">Aucun ticket gagnant.</p>
                  ) : (
                    <table className="rbl-ticket-table">
                      <thead>
                        <tr><th>Temp</th><th>T/No.</th><th>Produit</th><th>Numero</th><th>Mise</th><th>Gagne</th></tr>
                      </thead>
                      <tbody>
                        {lot.tickets_gagnants.map((t, i) => (
                          <tr key={i}>
                            <td>{t.time}</td>
                            <td>{t.online ? 'O' : 'L'} | {t.ticket_no ?? '—'}</td>
                            <td>{t.product_abbr}</td>
                            <td>{t.numbers}</td>
                            <td>{money(t.mise)}</td>
                            <td>{money(t.gagne)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}

              <hr className="rbl-dotted" />
            </div>
          ))}

          <hr className="rbl-solid" />
          <p className="rbl-section-title rbl-bold">Generaux</p>
          <div className="rbl-amount-row">
            <span>Vendu =</span>
            <span>{money(data.generaux.vendu)}</span>
          </div>
          <div className="rbl-amount-row">
            <span>Gagne =</span>
            <span>{money(data.generaux.gagne)}</span>
          </div>
          <hr className="rbl-dash" />
          <div className="rbl-amount-row rbl-bold">
            <span>Brut =</span>
            <span>{money(data.generaux.brut)}</span>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ResumeBanqueLotteryPage;
